import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import CompactCount from '../components/admin/CompactCount';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowUp, ArrowDown, ArrowUpDown, MoreHorizontal, Search, Users,
  Ban, CheckCircle2, Trash2, RotateCcw, Eye, AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';

const LIMIT = 20;

const STATUS_TABS = [
  { value: '', label: 'All', countKey: null },
  { value: 'active', label: 'Active', countKey: 'active' },
  { value: 'suspended', label: 'Suspended', countKey: 'suspended' },
  { value: 'deleted', label: 'Deleted Users', countKey: 'deleted' },
];

const STATUS_BADGE = {
  active: { label: 'Active', className: 'bg-emerald-600/15 text-emerald-600 border-emerald-600/30' },
  suspended: { label: 'Suspended', className: 'bg-amber-500/15 text-amber-600 border-amber-500/30' },
  deleted: { label: 'Soft-Deleted', className: 'bg-destructive/15 text-destructive border-destructive/30' },
};

const RELATED_LABELS = {
  favorites: 'Favorites',
  questions: 'Questions',
  answers: 'Answers',
  votes: 'Votes',
  reports_filed: 'Reports filed',
  feedback: 'Feedback',
  topic_requests: 'Topic requests',
};

const AUDIT_LABELS = {
  'user.suspend': 'Suspended',
  'user.unsuspend': 'Unsuspended',
  'user.soft_delete': 'Soft-deleted',
  'user.restore': 'Restored',
  'user.hard_delete': 'Permanently deleted',
};

const formatDate = (dateStr, pattern = 'MMM d, yyyy') => {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), pattern);
  } catch {
    return dateStr;
  }
};

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGE[status] || STATUS_BADGE.active;
  return <Badge variant="outline" className={`whitespace-nowrap ${s.className}`}>{s.label}</Badge>;
};

const SortableHead = ({ label, column, sort, order, onSort, className = '' }) => {
  const active = sort === column;
  const Icon = !active ? ArrowUpDown : order === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className} aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 hover:text-foreground"
        data-testid={`sort-${column}`}
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? 'text-autoxium-blue' : 'opacity-50'}`} />
      </button>
    </TableHead>
  );
};

// Which actions a row offers, by status. Self-actions are disabled (the server
// refuses them too -- this is only so the admin isn't shown a button that 400s).
const actionsFor = (u, isSelf) => {
  const list = [];
  if (u.status === 'active') list.push({ type: 'suspend', label: 'Suspend', icon: Ban, disabled: isSelf });
  if (u.status === 'suspended') list.push({ type: 'unsuspend', label: 'Unsuspend', icon: CheckCircle2 });
  if (u.status !== 'deleted') list.push({ type: 'soft-delete', label: 'Delete', icon: Trash2, danger: true, disabled: isSelf });
  if (u.status === 'deleted') {
    list.push({ type: 'restore', label: 'Restore', icon: RotateCcw });
    list.push({ type: 'hard-delete', label: 'Delete permanently', icon: AlertTriangle, danger: true, disabled: isSelf });
  }
  return list;
};

const AdminUsersPage = () => {
  const { user: me } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ active: 0, suspended: 0, deleted: 0 });
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [skip, setSkip] = useState(0);

  const [detail, setDetail] = useState(null); // full AdminUserDetailResponse
  const [detailLoading, setDetailLoading] = useState(false);

  const [confirm, setConfirm] = useState(null); // { type, user }
  const [reason, setReason] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [working, setWorking] = useState(false);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSkip(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getUsers({ skip, limit: LIMIT, search, status, sort, order });
      setItems(data.items);
      setTotal(data.total);
      setCounts(data.counts);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [skip, search, status, sort, order]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSort = (column) => {
    setSkip(0);
    if (sort === column) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(column);
      setOrder(column === 'name' || column === 'email' || column === 'role' ? 'asc' : 'desc');
    }
  };

  const openDetail = async (userId) => {
    setDetailLoading(true);
    setDetail({ user_id: userId });
    try {
      setDetail(await adminApi.getUser(userId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load user');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openConfirm = (type, u) => {
    setReason('');
    setConfirmEmail('');
    setConfirm({ type, user: u });
  };

  const runAction = async () => {
    if (!confirm) return;
    const { type, user: u } = confirm;
    setWorking(true);
    try {
      let updated = null;
      if (type === 'suspend') updated = await adminApi.suspendUser(u.user_id, reason.trim());
      else if (type === 'hard-delete') await adminApi.hardDeleteUser(u.user_id, confirmEmail.trim());
      else updated = await adminApi.userAction(u.user_id, type);

      toast.success({
        suspend: 'User suspended',
        unsuspend: 'Suspension lifted',
        'soft-delete': 'User moved to Deleted Users',
        restore: 'User restored',
        'hard-delete': 'User permanently deleted',
      }[type]);
      setConfirm(null);
      if (type === 'hard-delete') setDetail(null);
      else if (detail && detail.user_id === u.user_id) setDetail(updated);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setWorking(false);
    }
  };

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + LIMIT, total);
  const allCount = counts.active + counts.suspended;

  const confirmUser = confirm?.user;
  const hardDeleteReady = confirm?.type === 'hard-delete'
    && confirmEmail.trim().toLowerCase() === (confirmUser?.email || '').toLowerCase();

  const renderActionsMenu = (u) => {
    const isSelf = me?.user_id === u.user_id;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={`Actions for ${u.email}`} data-testid={`user-actions-${u.user_id}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => openDetail(u.user_id)}>
            <Eye className="h-4 w-4 mr-2" /> View details
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {actionsFor(u, isSelf).map(({ type, label, icon: Icon, danger, disabled }) => (
            <DropdownMenuItem
              key={type}
              disabled={disabled}
              onSelect={() => openConfirm(type, u)}
              className={danger ? 'text-destructive focus:text-destructive' : ''}
              data-testid={`user-action-${type}-${u.user_id}`}
            >
              <Icon className="h-4 w-4 mr-2" /> {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-users-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn" aria-label="Back to admin">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">Users</h1>
            <p className="text-muted-foreground text-sm">Registered users, login activity and account status</p>
          </div>
        </div>

        <Tabs value={status} onValueChange={(v) => { setSkip(0); setStatus(v); }} className="mb-4">
          <div className="overflow-x-auto -mx-4 px-4">
            <TabsList className="w-max">
              {STATUS_TABS.map(({ value, label, countKey }) => (
                <TabsTrigger key={value || 'all'} value={value} data-testid={`status-tab-${value || 'all'}`}>
                  {label}
                  <span className="ml-1.5 text-xs font-mono opacity-70">
                    <CompactCount value={countKey ? counts[countKey] : allCount} />
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative w-full sm:max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name or email"
              maxLength={100}
              className="pl-9"
              aria-label="Search users by name or email"
              data-testid="user-search-input"
            />
          </div>
          <span className="text-sm text-muted-foreground sm:ml-auto">
            {total === 0 ? '0 users' : `${from}-${to} of ${total}`}
          </span>
        </div>

        <Card className="border-2">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center" data-testid="no-users-message">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {search ? 'No users match your search.' : status === 'deleted' ? 'No deleted users.' : 'No users found.'}
                </p>
              </div>
            ) : (
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <SortableHead label="Name" column="name" sort={sort} order={order} onSort={handleSort} />
                    <SortableHead label="Email" column="email" sort={sort} order={order} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHead label="Role" column="role" sort={sort} order={order} onSort={handleSort} className="hidden sm:table-cell" />
                    <TableHead>Status</TableHead>
                    <SortableHead label="Registered" column="created_at" sort={sort} order={order} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortableHead label="Last login" column="last_login_at" sort={sort} order={order} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortableHead label="Logins" column="login_count" sort={sort} order={order} onSort={handleSort} className="hidden sm:table-cell" />
                    <TableHead className="text-right"><span className="sr-only">Actions</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="max-w-[220px]">
                        <button
                          type="button"
                          onClick={() => openDetail(u.user_id)}
                          className="text-left font-medium hover:text-autoxium-blue truncate block max-w-full"
                        >
                          {u.first_name} {u.last_name}
                          {me?.user_id === u.user_id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                        </button>
                        {/* Email moves under the name when its column is hidden. */}
                        <span className="md:hidden block text-xs text-muted-foreground truncate">{u.email}</span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell max-w-[240px] truncate text-sm">{u.email}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <span className={`text-xs font-mono uppercase ${u.role === 'admin' ? 'text-autoxium-blue font-semibold' : 'text-muted-foreground'}`}>
                          {u.role}
                        </span>
                      </TableCell>
                      <TableCell><StatusBadge status={u.status} /></TableCell>
                      <TableCell className="hidden lg:table-cell whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(u.created_at)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell whitespace-nowrap text-sm text-muted-foreground">
                        {formatDate(u.last_login_at, 'MMM d, yyyy h:mm a') || 'Never'}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell font-mono text-sm">
                        <CompactCount value={u.login_count} />
                      </TableCell>
                      <TableCell className="text-right">{renderActionsMenu(u)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {total > LIMIT && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" disabled={skip === 0}
              onClick={() => setSkip((s) => Math.max(0, s - LIMIT))} data-testid="users-prev-page-btn">
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={skip + LIMIT >= total}
              onClick={() => setSkip((s) => s + LIMIT)} data-testid="users-next-page-btn">
              Next
            </Button>
          </div>
        )}
      </div>

      {/* User detail */}
      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
          {detailLoading || !detail?.email ? (
            <div className="space-y-3 py-4">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-chivo uppercase flex flex-wrap items-center gap-2">
                  {detail.first_name} {detail.last_name}
                  <StatusBadge status={detail.status} />
                </DialogTitle>
                <DialogDescription className="break-all">{detail.email}</DialogDescription>
              </DialogHeader>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div><dt className="text-xs text-muted-foreground uppercase font-mono">Role</dt><dd>{detail.role}</dd></div>
                <div><dt className="text-xs text-muted-foreground uppercase font-mono">Mobile</dt><dd>{detail.mobile_number || '—'}</dd></div>
                <div><dt className="text-xs text-muted-foreground uppercase font-mono">Registered</dt><dd>{formatDate(detail.created_at)}</dd></div>
                <div>
                  <dt className="text-xs text-muted-foreground uppercase font-mono">Logins</dt>
                  <dd className="font-mono"><CompactCount value={detail.login_count} /></dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs text-muted-foreground uppercase font-mono">Last login</dt>
                  <dd>{formatDate(detail.last_login_at, 'MMM d, yyyy h:mm a') || 'Never'}</dd>
                </div>
                {detail.status === 'suspended' || detail.suspended_at ? (
                  <div className="col-span-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                    <dt className="text-xs text-amber-600 uppercase font-mono">
                      Suspended {formatDate(detail.suspended_at, 'MMM d, yyyy h:mm a')}
                    </dt>
                    <dd className="mt-1 whitespace-pre-wrap">{detail.suspend_reason || 'No reason given.'}</dd>
                  </div>
                ) : null}
                {detail.deleted_at && (
                  <div className="col-span-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
                    <dt className="text-xs text-destructive uppercase font-mono">
                      Soft-deleted {formatDate(detail.deleted_at, 'MMM d, yyyy h:mm a')}
                    </dt>
                    <dd className="mt-1">by {detail.deleted_by_name || detail.deleted_by || 'unknown admin'}</dd>
                  </div>
                )}
              </dl>

              <div>
                <h3 className="text-xs text-muted-foreground uppercase font-mono mb-2">Related data</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(RELATED_LABELS).map(([key, label]) => (
                    <div key={key} className="rounded-md border bg-muted/50 text-foreground px-2 py-1.5">
                      <div className="text-[10px] uppercase text-muted-foreground font-mono truncate">{label}</div>
                      <CompactCount value={detail.related_counts?.[key] ?? 0} className="font-mono font-semibold" />
                    </div>
                  ))}
                </div>
              </div>

              {detail.audit?.length > 0 && (
                <div>
                  <h3 className="text-xs text-muted-foreground uppercase font-mono mb-2">Moderation history</h3>
                  <ul className="space-y-1.5 text-sm">
                    {detail.audit.map((a) => (
                      <li key={`${a.action}-${a.created_at}`} className="flex flex-wrap gap-x-2">
                        <span className="font-medium">{AUDIT_LABELS[a.action] || a.action}</span>
                        <span className="text-muted-foreground">
                          by {a.admin_name || 'deleted admin'} · {formatDate(a.created_at, 'MMM d, yyyy h:mm a')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {actionsFor(detail, me?.user_id === detail.user_id).map(({ type, label, icon: Icon, danger, disabled }) => (
                  <Button
                    key={type}
                    size="sm"
                    variant={danger ? 'destructive' : 'outline'}
                    disabled={disabled}
                    onClick={() => openConfirm(type, detail)}
                    className="gap-1.5"
                    data-testid={`detail-action-${type}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </Button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation for every action */}
      <AlertDialog open={!!confirm} onOpenChange={(open) => { if (!open && !working) setConfirm(null); }}>
        <AlertDialogContent data-testid="user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">
              {{
                suspend: 'Suspend user?',
                unsuspend: 'Lift suspension?',
                'soft-delete': 'Delete user?',
                restore: 'Restore user?',
                'hard-delete': 'Permanently delete user?',
              }[confirm?.type]}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="font-medium text-foreground break-all">
                  {confirmUser?.first_name} {confirmUser?.last_name} · {confirmUser?.email}
                </p>
                {confirm?.type === 'suspend' && (
                  <p>They will be signed out on their next request and cannot log in until unsuspended. No data is removed.</p>
                )}
                {confirm?.type === 'unsuspend' && <p>They will be able to log in again straight away.</p>}
                {confirm?.type === 'soft-delete' && (
                  <p>
                    The account is hidden and cannot log in. All their data (favorites, questions, answers)
                    is kept, and you can restore it from the Deleted Users tab.
                  </p>
                )}
                {confirm?.type === 'restore' && <p>The account regains access with all of its data intact.</p>}
                {confirm?.type === 'hard-delete' && (
                  <>
                    <p className="text-destructive font-semibold">This cannot be undone.</p>
                    <p>
                      The account, favorites, votes, feedback, topic requests and newsletter subscription are
                      permanently removed. Their questions and answers stay on the site, shown as
                      “Deleted user”. The moderation audit log is kept.
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {confirm?.type === 'suspend' && (
            <div className="space-y-1">
              <label htmlFor="suspend-reason" className="text-sm font-medium">Reason (optional)</label>
              <Textarea
                id="suspend-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Shown to admins only"
                data-testid="suspend-reason-input"
              />
              <p className="text-xs text-muted-foreground text-right">{reason.length}/500</p>
            </div>
          )}

          {confirm?.type === 'hard-delete' && (
            <div className="space-y-1">
              <label htmlFor="confirm-email" className="text-sm">
                Type <span className="font-mono font-semibold break-all">{confirmUser?.email}</span> to confirm
              </label>
              <Input
                id="confirm-email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                data-testid="hard-delete-email-input"
              />
            </div>
          )}

          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={working}>Cancel</Button>
            <Button
              onClick={runAction}
              disabled={working || (confirm?.type === 'hard-delete' && !hardDeleteReady)}
              variant={['soft-delete', 'hard-delete', 'suspend'].includes(confirm?.type) ? 'destructive' : 'default'}
              data-testid="confirm-user-action-btn"
            >
              {working ? 'Working…' : {
                suspend: 'Suspend',
                unsuspend: 'Unsuspend',
                'soft-delete': 'Delete',
                restore: 'Restore',
                'hard-delete': 'Delete permanently',
              }[confirm?.type]}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsersPage;
